import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Store } from '@ngrx/store';
import { AppState } from '../../../ngrx/template/template.reducers';
import * as AuthActions from '../../../ngrx/auth/auth.actions';
import {
  selectAuthState,
  selectIsAuthenticated,
} from '../../../ngrx/auth/auth.selectors';
import { Subscription, BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthService } from '../../../services/auth/auth.service';
import {
  clearSkipSilentRefreshAfterLogout,
  shouldSkipSilentRefreshAfterLogout,
} from '../../../services/auth/auth-storage.constants';
import { LoadingSpinnerComponent } from '../../loading-spinner/loading-spinner.component';
import { CommonModule } from '@angular/common';
import { LoadingService } from '../../../services/loading/loading.service';
import { MatDialog } from '@angular/material/dialog';
import { AuthMethodModalComponent } from '../../modals/auth-method-modal/auth-method-modal.component';
import { firstValueFrom } from 'rxjs';
import { MatDialogModule } from '@angular/material/dialog';
import { DisplayService } from '../../../services/display/display.service';
import { Router } from '@angular/router';
import {
  captureOAuthContinueFromUrl,
  hasPendingOAuthContinue,
  isLoginRoute,
} from '../../../services/auth/oauth-continue.util';

@Component({
  selector: 'app-duna-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    LoadingSpinnerComponent,
  ],
  templateUrl: './duna-login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-login.component.scss',
})
export class DunaLoginComponent implements OnInit, OnDestroy {
  username: string = '';
  password: string = '';
  private subscriptions: Subscription[] = [];
  private hasAttemptedRefresh = false;
  isLoading$ = this.loadingService.isLoading$;

  constructor(
    private store: Store<AppState>,
    private authService: AuthService,
    private loadingService: LoadingService,
    private dialog: MatDialog,
    private displayService: DisplayService,
    private router: Router
  ) {}

  async onSubmit() {
    if (!this.username || !this.password) {
      this.displayService.showErrorMessage(
        'Please enter both username and password'
      );
      return;
    }

    this.loadingService.show();
    clearSkipSilentRefreshAfterLogout();

    try {
      const response = await firstValueFrom(
        this.authService.checkAuthMethods(this.username)
      );

      if (response.methods && response.methods.length > 1) {
        const dialogRef = this.dialog.open(AuthMethodModalComponent, {
          width: '400px',
          data: { methods: response.methods },
        });

        const result = await firstValueFrom(dialogRef.afterClosed());
        if (result) {
          this.store.dispatch(
            AuthActions.login({
              username: this.username,
              password: this.password,
              authMethodId: result.id,
              redirectUrl: sessionStorage.getItem('redirectUrl') || '/',
            })
          );
        } else {
          this.loadingService.hide();
        }
      } else {
        this.store.dispatch(
          AuthActions.login({
            username: this.username,
            password: this.password,
            authMethodId: response.methods[0].id,
            redirectUrl: sessionStorage.getItem('redirectUrl') || '/',
          })
        );
      }
    } catch (error) {
      this.loadingService.hide();
      this.displayService.showErrorMessage(
        'Error checking authentication methods'
      );
    }
  }

  ngOnInit() {
    const query = this.router.url.includes('?')
      ? this.router.url.substring(this.router.url.indexOf('?'))
      : window.location.search;
    captureOAuthContinueFromUrl(query);

    const onLoginRoute = isLoginRoute(this.router.url);
    const oauthPending = hasPendingOAuthContinue();
    const isAuthenticated = this.authService.isAuthenticated;

    if (isAuthenticated && onLoginRoute && !oauthPending) {
      // Already logged in (normal visit to /login) — redirect away from login
      this.store
        .select((state) => state.auth)
        .pipe(take(1))
        .subscribe((authState) => {
          if (authState.token && authState.user) {
            this.store.dispatch(
              AuthActions.loginSuccess({
                token: authState.token,
                user: authState.user,
                isAuthenticated: true,
                shouldNavigate: true,
              })
            );
          } else if (!this.hasAttemptedRefresh) {
            this.hasAttemptedRefresh = true;
            this.authService.refreshTokenSilently().catch((error) => {
              console.error(
                '[DunaLoginComponent] Failed to refresh token:',
                error
              );
            });
          }
        });
      return;
    }

    // OAuth / SSO: only a valid API refresh cookie can complete authorize (see auth.service init)
    if (
      onLoginRoute &&
      !this.hasAttemptedRefresh &&
      (oauthPending || !isAuthenticated)
    ) {
      this.hasAttemptedRefresh = true;
      if (!shouldSkipSilentRefreshAfterLogout()) {
        this.authService.refreshTokenSilently().catch(() => {
          // Expected when no refresh cookie exists
        });
      } else {
        clearSkipSilentRefreshAfterLogout();
      }
    }

    this.subscriptions.push(
      this.store.select(selectIsAuthenticated).subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this.loadingService.hide();
        }
      })
    );

    this.subscriptions.push(
      this.store
        .select((state) => state.auth.error)
        .subscribe((error) => {
          if (error) {
            console.error('[DunaLoginComponent] Auth error:', error);
            this.loadingService.hide();
            this.displayService.showErrorMessage(error);
          }
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
